import { createStore } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {
    Row,
    TableInstance,
    UseColumnOrderInstanceProps,
    UseExpandedInstanceProps,
    UseFiltersInstanceProps,
    UseGlobalFiltersInstanceProps,
    UseGroupByInstanceProps,
    UsePaginationInstanceProps,
    UseRowSelectInstanceProps,
    UseRowStateInstanceProps,
    UseSortByInstanceProps,
} from 'react-table'

interface SparkTableInstance<D extends Record<string, unknown> = Record<string, unknown>>
    extends TableInstance<D>,
        UseColumnOrderInstanceProps<D>,
        UseExpandedInstanceProps<D>,
        UseFiltersInstanceProps<D>,
        UseGlobalFiltersInstanceProps<D>,
        UseGroupByInstanceProps<D>,
        UsePaginationInstanceProps<D>,
        UseRowSelectInstanceProps<D>,
        UseRowStateInstanceProps<D>,
        UseSortByInstanceProps<D> {}

/** Don't use tanstack rowId for key since it may not be stable across pages */
type RowKey = string
type RowState = Record<string, unknown>
type RowStates = Record<RowKey, RowState>

type TableStoreState = {
    rowStates: RowStates
    activeRowKey: string | null
    tableInstance: SparkTableInstance | null
}

type TableStoreActions = {
    // Row States
    setRowState: (key: RowKey, rowState: RowState) => void
    clearAllRowStates: () => void
    getRowState: (key: RowKey) => RowState | undefined
    getActiveRowState: () => RowState | undefined
    getErrorCount: () => number

    // Active Row
    setActiveRowKey: (key: RowKey) => void
    setFirstSelectedAsActiveRow: () => void
    clearActiveRowKey: () => void
    getActiveRow: () => Row<Record<string, unknown>> | undefined
    getRowByKey: (key: string) => Row<Record<string, unknown>> | undefined
    getRowByIndex: (index: number) => Row<Record<string, unknown>> | undefined
    getIsRowActive: (key: string) => boolean
    getIsRowSelected: (key: string) => boolean

    // Active Row By Index
    getActiveRowIndex: () => number
    getIsFirstPage: () => boolean
    getIsLastPage: () => boolean
    getCanIncrementActiveRowByIndex: () => boolean
    getCanDecrementActiveRowByIndex: () => boolean
    incrementActiveRowByIndex: () => void
    decrementActiveRowByIndex: () => void

    // Table Instance
    setTableInstance: (tableInstance: TableInstance<any>) => void
    getSelectedRowCount: () => number
}

export type TableStoreAndActions = TableStoreState & TableStoreActions

/** Store only available through useTableContext */
export const createTableStore = (rowKey: RowKey, initProps?: Partial<TableStoreState>) => {
    const DEFAULT_PROPS: TableStoreState = {
        rowStates: {},
        activeRowKey: null,
        tableInstance: null,
    }
    return createStore<TableStoreAndActions>()(
        devtools(
            (set, get) => ({
                ...DEFAULT_PROPS,
                ...initProps,

                // Row States
                setRowState: (key, rowState) =>
                    set((state) => ({
                        rowStates: { ...state.rowStates, [key]: rowState },
                    })),
                clearAllRowStates: () => set((state) => (state.rowStates = {})),
                getRowState: (key) => {
                    const rowState = get().rowStates[key]

                    if (!rowState) {
                        return undefined
                    }

                    return rowState
                },
                getErrorCount: () => {
                    const rowStates = get().rowStates
                    const errors = Object.values(rowStates).filter((rowState) => rowState.status === 'error')
                    return errors.length
                },

                // Active Row Key
                setActiveRowKey: (key) =>
                    typeof key === 'string' ? set({ activeRowKey: key }) : set({ activeRowKey: null }),
                setFirstSelectedAsActiveRow: () => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return
                    }

                    const nextRowKey = ti?.selectedFlatRows?.[0].original[rowKey]

                    return typeof nextRowKey === 'string'
                        ? set({ activeRowKey: nextRowKey })
                        : set({ activeRowKey: null })
                },
                clearActiveRowKey: () => set({ activeRowKey: null }),
                getActiveRow: () => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return undefined
                    }
                    const activeRowKey = get().activeRowKey
                    if (!activeRowKey) {
                        return undefined
                    }
                    const activeRow = ti.selectedFlatRows.find((row) => row.original[rowKey] === activeRowKey)
                    return activeRow
                },
                getActiveRowState: () => {
                    const key = get().activeRowKey
                    if (!key) {
                        return undefined
                    }
                    const rowState = get().rowStates[key]

                    if (!rowState) {
                        return undefined
                    }
                    return rowState
                },
                getRowByKey: (key) => {
                    if (!key) {
                        return undefined
                    }
                    const ti = get().tableInstance
                    if (!ti) {
                        return undefined
                    }

                    const foundRow = ti.rows.find((row) => row.original[rowKey] === key)
                    return foundRow
                },
                getRowByIndex: (index) => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return undefined
                    }
                    const row = ti.rows[index]
                    return row
                },
                getIsRowActive: (key) => {
                    const activeRowKey = get().activeRowKey
                    if (!activeRowKey) {
                        return false
                    }
                    return activeRowKey === key
                },
                getIsRowSelected: (key) => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return false
                    }

                    const foundRow = ti.rows.find((row) => row.original[rowKey] === key)

                    if (!foundRow) {
                        return false
                    }

                    return 'isSelected' in foundRow && !!foundRow.isSelected
                },

                // Active Row By Index
                getActiveRowIndex: () => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return -1
                    }
                    const activeRowKey = get().activeRowKey
                    if (!activeRowKey) {
                        return -1
                    }
                    const rowIndex = ti.selectedFlatRows.findIndex((row) => row.original[rowKey] === activeRowKey)
                    return rowIndex
                },
                incrementActiveRowByIndex: () => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return null
                    }
                    const activeRowKey = get().activeRowKey
                    if (!activeRowKey) {
                        return null
                    }
                    const rowIndex = ti.selectedFlatRows.findIndex((row) => row.original[rowKey] === activeRowKey)
                    if (rowIndex === -1) {
                        return null
                    }
                    const nextRowIndex = rowIndex + 1
                    const nextRowKey = ti.selectedFlatRows[nextRowIndex]?.original[rowKey]

                    return typeof nextRowKey === 'string'
                        ? set({ activeRowKey: nextRowKey })
                        : set({ activeRowKey: null })
                },
                decrementActiveRowByIndex: () => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return null
                    }
                    const activeRowKey = get().activeRowKey
                    if (!activeRowKey) {
                        return null
                    }
                    const rowIndex = ti.selectedFlatRows.findIndex((row) => row.original[rowKey] === activeRowKey)
                    const nextRowIndex = rowIndex - 1
                    const nextRowKey = ti.selectedFlatRows[nextRowIndex]?.original[rowKey]

                    return typeof nextRowKey === 'string'
                        ? set({ activeRowKey: nextRowKey })
                        : set({ activeRowKey: null })
                },
                getIsFirstPage: () => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return false
                    }
                    const selectedRowCount = ti.selectedFlatRows.length || 0
                    if (!selectedRowCount) {
                        return false
                    }
                    const activeRowKey = get().activeRowKey
                    if (!activeRowKey) {
                        return false
                    }
                    const activeRowIndex = ti.selectedFlatRows.findIndex((row) => row.original[rowKey] === activeRowKey)
                    return activeRowIndex === 0
                },
                getIsLastPage: () => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return false
                    }
                    const selectedRowCount = ti.selectedFlatRows.length || 0
                    if (!selectedRowCount) {
                        return false
                    }
                    const activeRowKey = get().activeRowKey
                    if (!activeRowKey) {
                        return false
                    }
                    const activeRowIndex = ti.selectedFlatRows.findIndex((row) => row.original[rowKey] === activeRowKey)
                    return activeRowIndex === selectedRowCount - 1
                },
                getCanIncrementActiveRowByIndex: () => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return false
                    }
                    const selectedRowCount = ti.selectedFlatRows.length || 0
                    if (!selectedRowCount) {
                        return false
                    }
                    const activeRowKey = get().activeRowKey
                    if (!activeRowKey) {
                        return false
                    }
                    const activeRowIndex = ti.selectedFlatRows.findIndex((row) => row.original[rowKey] === activeRowKey)
                    return activeRowIndex < selectedRowCount - 1
                },
                getCanDecrementActiveRowByIndex: () => {
                    const ti = get().tableInstance
                    if (!ti) {
                        return false
                    }
                    const selectedRowCount = ti.selectedFlatRows.length || 0
                    if (!selectedRowCount) {
                        return false
                    }
                    const activeRowKey = get().activeRowKey
                    if (!activeRowKey) {
                        return false
                    }
                    const activeRowIndex = ti.selectedFlatRows.findIndex((row) => row.original[rowKey] === activeRowKey)
                    return activeRowIndex > 0
                },

                // Table Instance
                setTableInstance: (tableInstance) => set({ tableInstance: tableInstance as any }),
                getSelectedRowCount: () => get().tableInstance?.selectedFlatRows.length || 0,
            }),
            { name: 'TableStore' },
        ),
    )
}

export type TableStore = ReturnType<typeof createTableStore>
